// src/js/index.ts
import { createRequiredContext } from "@enymo/react-better-context";
import useSocket from "@enymo/react-socket-hook";
import { requireNotNull } from "@enymo/ts-nullsafe";
import { AxiosError } from "axios";
import pluralize from "pluralize";
import { useMemo } from "react";

// src/js/util.ts
function filter(input) {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== void 0));
}
var identity = (input) => input;
function isAtomic(input, reactNative) {
  return isFile(input, reactNative) || input === null || typeof input !== "object";
}
function isFile(input, reactNative) {
  return input instanceof File || reactNative && "uri" in input && "name" in input && "type" in input;
}
function objectNeedsFormDataConversion(input, reactNative) {
  if (isAtomic(input, reactNative)) {
    return isFile(input, reactNative);
  } else {
    for (const value of Array.isArray(input) ? input : Object.values(input)) {
      if (objectNeedsFormDataConversion(value, reactNative)) {
        return true;
      }
    }
    return false;
  }
}
function objectToFormDataRecursive(input, reactNative, fd, path) {
  if (isAtomic(input, reactNative)) {
    switch (input) {
      case void 0:
        break;
      case null:
        fd.append(path, "");
        break;
      case true:
        fd.append(path, "1");
        break;
      case false:
        fd.append(path, "0");
        break;
      default:
        fd.append(path, input);
    }
  } else {
    for (const [key, value] of Array.isArray(input) ? input.entries() : Object.entries(input)) {
      objectToFormDataRecursive(value, reactNative, fd, `${path}[${key}]`);
    }
  }
}
function objectToFormData(input, reactNative) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(input)) {
    objectToFormDataRecursive(value, reactNative, fd, key);
  }
  return fd;
}
function dateTransformer(input) {
  return Object.fromEntries(Object.entries(input).map(([key, value]) => [
    key,
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}T(?:\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|(?:\+|-)\d{2}:\d{2})?)?$/.test(value) ? new Date(value) : value
  ]));
}
var pad = (n) => `${Math.floor(Math.abs(n))}`.padStart(2, "0");
function toISOStringWithTimezone(date) {
  const tzOffset = -date.getTimezoneOffset();
  const diff = tzOffset >= 0 ? "+" : "-";
  return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate()) + "T" + pad(date.getHours()) + ":" + pad(date.getMinutes()) + ":" + pad(date.getSeconds()) + diff + pad(tzOffset / 60) + ":" + pad(tzOffset % 60);
}
function inverseDateTransformer(input) {
  return Object.fromEntries(Object.entries(input).map(([key, value]) => [key, value instanceof Date ? toISOStringWithTimezone(value) : value]));
}
function conditionalApply(input, func, apply) {
  return apply ? func(input) : input;
}
function unwrap(input) {
  if ("data" in input && typeof input.data === "object" && input.data !== null) {
    return {
      data: input.data,
      meta: "meta" in input ? input.meta : null
    };
  } else {
    return {
      data: input,
      meta: null
    };
  }
}

// src/js/index.ts
var [WebResourceProvider, useContext] = createRequiredContext("WebResourceProvider must be present in component tree");
function createWebResourceAdapter({
  reactNative = false,
  paramNameCallback,
  eventNameCallback
}) {
  return (resource, {
    paramName: paramNameOverride,
    socketEvent: eventOverride,
    transformer: baseTransformer = identity,
    inverseTransformer: baseInverseTransformer = identity,
    transformDates = false
  }) => {
    const transformer = transformDates ? async (item) => dateTransformer(await baseTransformer(item)) : baseTransformer;
    const inverseTransformer = transformDates ? async (item) => inverseDateTransformer(await baseInverseTransformer(item)) : baseInverseTransformer;
    return {
      actionHook: ({
        useFormData
      }, params) => {
        const { axios, routeFunction } = useContext();
        const paramName = useMemo(() => paramNameOverride ?? (resource && (paramNameCallback?.(resource) ?? pluralize.singular(requireNotNull(resource.split(".").pop())).replace(/-/g, "_"))), [paramNameOverride, resource]);
        return useMemo(() => ({
          async store(data, config) {
            const body = conditionalApply(await inverseTransformer(data), inverseDateTransformer, transformDates);
            return transformer(unwrap((await axios.post(routeFunction(`${resource}.store`, params), useFormData || objectNeedsFormDataConversion(body, reactNative) ? objectToFormData(body, reactNative) : body, useFormData ? {
              ...config,
              headers: {
                ...config?.headers,
                "content-type": "multipart/form-data"
              }
            } : config)).data).data);
          },
          async batchStore(data, config) {
            const body = {
              _batch: await Promise.all(data.map(async (resource2) => filter(await inverseTransformer(resource2))))
            };
            return Promise.all(unwrap((await axios.post(routeFunction(`${resource}.batch.store`, params), useFormData || objectNeedsFormDataConversion(body, reactNative) ? objectToFormData(body, reactNative) : body, useFormData ? {
              ...config,
              headers: {
                ...config?.headers,
                "content-type": "multipart/form-data"
              }
            } : config)).data).data.map(transformer));
          },
          async update(id, data, config) {
            const body = filter(await inverseTransformer(data));
            const route = routeFunction(`${resource}.update`, {
              [paramName]: id,
              ...params
            });
            return transformer(unwrap((await (useFormData || objectNeedsFormDataConversion(body, reactNative) ? axios.post(route, objectToFormData({
              ...body,
              _method: "put"
            }, reactNative), {
              ...config,
              headers: {
                ...config?.headers,
                "content-type": "multipart/form-data"
              }
            }) : axios.put(route, body, config))).data).data);
          },
          async batchUpdate(data, config) {
            const body = {
              _batch: await Promise.all(data.map(async (resource2) => filter(await inverseTransformer(resource2))))
            };
            const route = routeFunction(`${resource}.batch.update`, params);
            return Promise.all(unwrap((await (useFormData || objectNeedsFormDataConversion(body, reactNative) ? axios.post(route, objectToFormData({
              ...body,
              _method: "put"
            }, reactNative), {
              ...config,
              headers: {
                ...config?.headers,
                "content-type": "multipart/form-data"
              }
            }) : axios.put(route, body, config))).data).data.map(inverseTransformer));
          },
          async destroy(id, config) {
            await axios.delete(routeFunction(`${resource}.destroy`, {
              [paramName]: id,
              ...params
            }), config);
          },
          async batchDestroy(ids, config) {
            await axios.delete(routeFunction(`${resource}.batch.destroy`, {
              ids,
              ...params
            }), config);
          },
          async query(action, data, params2, config) {
            const response = await axios.post(routeFunction(`${resource}.${action}`, params2), data, config);
            return {
              data: unwrap(response.data).data,
              destroy: response.headers["x-resource-destroy"]?.split(",").map((id) => {
                const [, type, value] = requireNotNull(id.match(/(i|s):(\d)+/), "Unable to parse x-resource-destroy header");
                switch (type) {
                  case "i":
                    return Number(value);
                  case "s":
                    return value;
                  default:
                    throw new Error("Unable to parse x-resource-destroy header");
                }
              }) ?? [],
              update: response.headers["x-resource-update"] ?? "merge"
            };
          },
          async refresh(id, config, signal) {
            try {
              const response = unwrap((await axios.get(id !== void 0 ? routeFunction(`${resource}.show`, {
                [paramName]: id,
                ...params
              }) : routeFunction(`${resource}.index`, params), { ...config, signal })).data);
              return {
                meta: response.meta,
                data: await (Array.isArray(response.data) ? Promise.all(response.data.map(transformer)) : transformer(response.data)),
                error: null
              };
            } catch (e) {
              if (e instanceof AxiosError) {
                return {
                  data: id !== void 0 ? null : [],
                  meta: null,
                  error: e
                };
              } else {
                throw e;
              }
            }
          }
        }), [useFormData, params]);
      },
      eventHook: (params, event, handler, dependencies) => {
        const eventBase = useMemo(() => eventOverride ?? eventNameCallback?.(resource, params) ?? resource?.split(".").map((part) => {
          const singular = pluralize.singular(part).replaceAll("-", "_");
          return params && singular in params ? `${part}.${params[singular]}` : part;
        }).join(".") ?? null, [params]);
        useSocket(handler ? `${eventBase}.${event}` : null, handler ?? (() => {
        }), dependencies);
      }
    };
  };
}
export {
  WebResourceProvider,
  createWebResourceAdapter as default
};
